<script setup lang="ts">
import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import "@xterm/xterm/css/xterm.css";
import { onMounted, ref, watch, onBeforeUnmount } from "vue";
import { invoke } from '@tauri-apps/api/core';
import { listen } from "@tauri-apps/api/event";
import { interpolateColor } from '../../utils/color.js';
import { createTelnetOutputNormalizer } from './telnetOutput.js';
import { createTelnetInputEcho, normalizeTelnetInput } from './telnetInput.js';
import { deviceTelnetArgs } from '../../services/httpTransfer.js';

const terminalEl = ref<HTMLDivElement | null>(null);
let term: Terminal;
let fitAddon: FitAddon;
const outputNormalizer = createTelnetOutputNormalizer();
const inputEcho = createTelnetInputEcho();

const props = defineProps({
    popupState: Boolean, // Detect when tab opens
    settings: {
        type: Object,
        required: true,
    }
});

function buildTheme(mc: string) {
  return {
    background:          interpolateColor(mc, '#000000', 0.80),
    foreground:          '#e2e8f0',
    cursor:              interpolateColor(mc, '#00ffff', 0.55),
    selectionBackground: interpolateColor(mc, '#ffffff', 0.20),
    black:               interpolateColor(mc, '#000000', 0.15),
    brightBlack:         interpolateColor(mc, '#ffffff', 0.25),
    cyan:                interpolateColor(mc, '#00ffff', 0.55),
    brightCyan:          interpolateColor(mc, '#00ffff', 0.70),
    green: '#4ade80', brightGreen: '#86efac',
    red:   '#f87171', brightRed:   '#fca5a5',
    yellow: '#fbbf24', brightYellow: '#fde68a',
  };
}

onMounted(async () => {
  term = new Terminal({
    cursorBlink: true,
    fontSize: 12,
    fontFamily: "monospace",
    theme: buildTheme(props.settings?.ui?.main_color ?? '#092e53'),
  });

  fitAddon = new FitAddon();
  term.loadAddon(fitAddon);

  term.open(terminalEl.value!);
  fitAddon.fit(); // initial fit

  // Luister naar telnet output van Rust
  await listen<string>("telnet-output", (event) => {
    const output = outputNormalizer.push(event.payload);
    if (output) term.write(output);
  });

  // Input van user naar backend sturen
  term.onData((data) => {
    // De openHASP Telnet-console echoot ingevoerde tekens niet terug.
    // Toon invoer lokaal, inclusief newline- en backspace-afhandeling.
    const echoedInput = inputEcho.push(data);
    if (echoedInput) term.write(echoedInput);
    invoke("send_telnet_command_command", { cmd: normalizeTelnetInput(data) });
  });

  // Resize observer to fit terminal on container resize
  const resizeObserver = new ResizeObserver(() => {
    fitAddon.fit();
  });
  if (terminalEl.value) {
    resizeObserver.observe(terminalEl.value);
  }

  onBeforeUnmount(() => {
    resizeObserver.disconnect();
  });
});

watch(() => props.settings?.ui?.main_color, (mc) => {
  if (term && mc) term.options.theme = buildTheme(mc);
});

async function connect() {
  const { addr, port } = deviceTelnetArgs(props.settings?.plate ?? {});
  await invoke("connect_telnet_command", { addr, port });
  term.writeln("\r\n*** Connecting to OpenHASP plate... ***\r\n");
}

async function disconnect() {
  inputEcho.reset();
  const trailingOutput = outputNormalizer.flush();
  if (trailingOutput) term.write(trailingOutput);
  await invoke("disconnect_telnet_command");
  term.writeln("\r\n*** Disconnected from OpenHASP plate ***\r\n");
}
</script>

<template>
  <div class="terminal-container">
    <div ref="terminalEl" class="terminal"></div>
    <div class="flex gap-3 mt-2 pt-2 border-t border-white/20">
      <button class="btn-connect" @click="connect">Connect</button>
      <button class="btn-disconnect" @click="disconnect">Disconnect</button>
    </div>
  </div>
</template>

<style scoped>
.terminal-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.terminal {
  flex: 1;
  border-radius: 4px;
  overflow: hidden;
}

.btn-connect {
  width: 8rem;
  font-size: 0.75rem;
  color: white;
  background-color: var(--ui-active);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 4px;
  height: 1.75rem;
  cursor: pointer;
  transition: opacity 0.15s;
}
.btn-connect:hover { opacity: 0.85; }

.btn-disconnect {
  width: 8rem;
  font-size: 0.75rem;
  color: #e2e8f0;
  background-color: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 4px;
  height: 1.75rem;
  cursor: pointer;
  transition: background-color 0.15s;
}
.btn-disconnect:hover { background-color: rgba(255,255,255,0.18); }
</style>
